import { api } from './client'
import type { Contact, ContactDealLink, CursorPage } from './types'

export async function listContacts(
  cursor?: string | null,
  limit = 50,
  q?: string,
): Promise<CursorPage<Contact>> {
  const { data } = await api.get<CursorPage<Contact>>('/api/contacts', {
    params: { cursor: cursor ?? undefined, limit, q: q || undefined },
  })
  return data
}

/** A page of contacts for the offset-paginated table view (GET /api/contacts/page). */
export interface ContactPage {
  items: Contact[]
  total: number
  page: number
  size: number
}

export type ContactSort =
  | 'name'
  | 'email'
  | 'phone'
  | 'leadSource'
  | 'owner'
  | 'createdAt'
  | 'lastContactedAt'
  | 'nextContactDate'
export type SortDir = 'asc' | 'desc'

/** The shared filter (owner + search + source) behind the table page, its id list and its sources. */
export interface ContactFilterParams {
  q?: string
  /** Owner user ids to filter by (repeatable / CSV on the wire). */
  owners?: string[]
  source?: string
  /** Restrict to the members of this list (manual members or a dynamic list's computed members). */
  list?: string
  /** A built-in view on top of the filter (server-side, so totals, export and select-all agree). */
  view?: ContactPresetView
}

/** Built-in views of the contacts table. */
export type ContactPresetView = 'mine' | 'unassigned' | 'recent' | 'stale' | 'no_next_contact' | 'missing_details'

export interface ContactPageParams extends ContactFilterParams {
  sort?: ContactSort
  dir?: SortDir
  /** 0-based page index. */
  page?: number
  size?: number
}

/** Serialize the shared filter to query params (owners as CSV, blanks dropped). */
function filterParams(params: ContactFilterParams) {
  return {
    q: params.q || undefined,
    owners: params.owners && params.owners.length ? params.owners.join(',') : undefined,
    source: params.source || undefined,
    listId: params.list || undefined,
    view: params.view || undefined,
  }
}

export async function listContactsPage(params: ContactPageParams): Promise<ContactPage> {
  const { data } = await api.get<ContactPage>('/api/contacts/page', {
    params: {
      ...filterParams(params),
      sort: params.sort,
      dir: params.dir,
      page: params.page ?? 0,
      size: params.size ?? 25,
    },
  })
  return data
}

/**
 * The ids of every contact matching the current filter (owner + search + source) — for the table's
 * "select all matching" so bulk actions can span the whole filtered set, not just the loaded page.
 */
export async function listContactIds(params: ContactFilterParams): Promise<string[]> {
  const { data } = await api.get<string[]>('/api/contacts/ids', { params: filterParams(params) })
  return data
}

/**
 * Distinct lead sources for the Source filter. When {@code owners} is given, only that owner's
 * sources are returned, so the Source options track the selected Owner.
 */
export async function listContactSources(owners?: string[]): Promise<string[]> {
  const { data } = await api.get<string[]>('/api/contacts/sources', {
    params: { owners: owners && owners.length ? owners.join(',') : undefined },
  })
  return data
}

export interface CreateContactParams {
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  mobile?: string
  leadSource?: string
  custom?: Record<string, unknown>
}

export async function createContact(params: CreateContactParams): Promise<Contact> {
  const { data } = await api.post<Contact>('/api/contacts', params)
  return data
}

export async function getContact(id: string): Promise<Contact> {
  const { data } = await api.get<Contact>(`/api/contacts/${id}`)
  return data
}

export interface UpdateContactParams {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  phone?: string | null
  mobile?: string | null
  ownerUserId?: string | null
  leadSource?: string | null
  status?: string | null
  birthday?: string | null
  nextContactDate?: string | null
  doNotEmail?: boolean
  doNotCall?: boolean
  doNotSms?: boolean
  custom?: Record<string, unknown>
}

export async function updateContact(id: string, params: UpdateContactParams): Promise<Contact> {
  const { data } = await api.put<Contact>(`/api/contacts/${id}`, params)
  return data
}

export async function deleteContact(id: string): Promise<void> {
  await api.delete(`/api/contacts/${id}`)
}

/** Download the contacts matching the current filter as a CSV blob. */
export async function exportContactsCsv(params: ContactFilterParams): Promise<Blob> {
  const res = await api.get('/api/contacts/export', { params: filterParams(params), responseType: 'blob' })
  return res.data as Blob
}

/** Merge the source contact into the target (kept); the source is deleted. Admin-only. */
export async function mergeContact(targetId: string, sourceId: string): Promise<Contact> {
  return (await api.post<Contact>(`/api/contacts/${targetId}/merge`, { sourceId })).data
}

/** Reassign a batch of contacts to one owner (or null to unassign). Admin-only. */
export async function bulkAssignOwner(
  contactIds: string[],
  ownerUserId: string | null,
): Promise<{ updated: number }> {
  return (await api.post<{ updated: number }>('/api/contacts/bulk/assign-owner', { contactIds, ownerUserId })).data
}

/** Likely duplicates of a contact (shared email or phone/mobile). */
export async function listContactDuplicates(id: string): Promise<Contact[]> {
  return (await api.get<Contact[]>(`/api/contacts/${id}/duplicates`)).data
}

/** Existing contacts sharing an e-mail or phone with one being typed in (up to 5) — the pre-save warning. */
export async function listContactMatches(email?: string, phone?: string): Promise<Contact[]> {
  const { data } = await api.get<Contact[]>('/api/contacts/matches', {
    params: { email: email || undefined, phone: phone || undefined },
  })
  return data
}

export async function listContactDeals(contactId: string): Promise<ContactDealLink[]> {
  const { data } = await api.get<ContactDealLink[]>(`/api/contacts/${contactId}/deals`)
  return data
}
